import { apiService } from './apiService';
import { API_CONFIG } from '../config/api';
import { apiUtils } from '../utils/apiUtils';

// Function to extract measurement from potentially nested response
function extractMeasurementData(response) {
  // If response has a 'measurement' property, use that
  if (response && response.measurement) {
    console.log('📦 Extracting nested measurement data');
    return response.measurement;
  }
  // Otherwise, assume the response is the measurement itself
  return response;
}

// Function to transform array-based sensor data into individual sensor entries
function transformMeasurementData(measurementData, options = {}) {
  if (!measurementData || typeof measurementData !== 'object') {
    return measurementData;
  }

  // Default options
  const {
    preserveBME688Hierarchy = true, // Keep BME688 as hierarchical by default
    transformOtherArrays = true
  } = options;

  const transformed = {};

  Object.entries(measurementData).forEach(([sensorName, sensorData]) => {
    if (sensorName === 'bme688' && Array.isArray(sensorData)) {
      if (preserveBME688Hierarchy) {
        // Keep BME688 as hierarchical array
        transformed[sensorName] = sensorData;
      } else {
        // Transform BME688 array into individual sensors (old behavior)
        sensorData.forEach((measurement) => {
          if (measurement && measurement.meas_profile) {
            const profileName = `${sensorName}-${measurement.meas_profile}`;
            // Create a clean copy without the meas_profile field
            const { meas_profile, ...cleanData } = measurement;
            transformed[profileName] = cleanData;
          }
        });
      }
    } else if (Array.isArray(sensorData) && transformOtherArrays) {
      // Handle other potential array-based sensors
      sensorData.forEach((measurement, index) => {
        const indexedName = `${sensorName}-${index + 1}`;
        transformed[indexedName] = measurement;
      });
    } else {
      // Keep other sensors as-is
      transformed[sensorName] = sensorData;
    }
  });

  return transformed;
}

// Function to transform a full measurement response
function transformMeasurementResponse(response, options = {}) {
  if (!response) return response;

  if (response.measurement_data) {
    return {
      ...response,
      measurement_data: transformMeasurementData(response.measurement_data, options),
      // Keep original data for reference if needed
      _original_measurement_data: response.measurement_data
    };
  }

  return response;
}

export const measurementsService = {
  // Get latest measurements for a specific box
  getLatestMeasurements: async (boxId, options = {}) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      const response = await apiService.get(API_CONFIG.ENDPOINTS.LATEST_MEASUREMENTS(boxId));

      // Extract the actual measurement data from potentially nested response
      const measurementData = extractMeasurementData(response);

      console.log('📊 Extracted measurement data:', measurementData);

      // Validate the response structure
      const validation = apiUtils.validateMeasurementData(measurementData);
      if (!validation.valid) {
        console.warn('Invalid measurement data structure:', validation.error, measurementData);
        // Return a formatted response even if validation fails
        return {
          box_id: boxId,
          measurement_data: {},
          timestamp: new Date().toISOString(),
          error: validation.error
        };
      }

      const parsedResponse = apiUtils.parseApiResponse(measurementData, 'measurements');

      // Default options preserve BME688 hierarchy
      const transformOptions = {
        preserveBME688Hierarchy: true,
        ...options
      };

      return transformMeasurementResponse(parsedResponse, transformOptions);
    } catch (error) {
      console.error(`Error fetching latest measurements for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getLatestMeasurements');
    }
  },

  // Get individual sensor measurements with time range
  getSensorMeasurementsWithRange: async (boxId, sensorId, startDate, endDate, params = {}) => {
    try {
      if (!boxId || !sensorId) {
        throw new Error('Box ID and sensor ID are required');
      }

      // Format dates to ISO string if they're Date objects
      const formattedStartDate = startDate instanceof Date ? startDate.toISOString() : startDate;
      const formattedEndDate = endDate instanceof Date ? endDate.toISOString() : endDate;

      // Build query parameters
      const queryParams = {
        ...params
      };

      // Add date range parameters if provided
      if (formattedStartDate) {
        queryParams.start = formattedStartDate;
      }
      if (formattedEndDate) {
        queryParams.end = formattedEndDate;
      }

      // Construct URL with sensor_id
      const baseUrl = `/ucsnose/api/measurements/${boxId}`;
      const urlParams = new URLSearchParams({ sensor_id: sensorId, ...queryParams });
      const fullUrl = `${baseUrl}?${urlParams.toString()}`;

      const response = await apiService.get(fullUrl);

      // Handle array response (typical for time series data)
      if (Array.isArray(response)) {
        return response.map(item => {
          const extracted = extractMeasurementData(item);
          return apiUtils.parseApiResponse(extracted, 'measurements');
        });
      }

      // Handle single response
      const extracted = extractMeasurementData(response);
      return apiUtils.parseApiResponse(extracted, 'measurements');
    } catch (error) {
      console.error(`Error fetching sensor measurements for ${sensorId} in box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getSensorMeasurementsWithRange');
    }
  },

  // Get latest measurements with BME688 flattened (for backward compatibility)
  getLatestMeasurementsFlat: async (boxId) => {
    return measurementsService.getLatestMeasurements(boxId, {
      preserveBME688Hierarchy: false
    });
  },

  // Get all measurements for a specific box with optional parameters
  getBoxMeasurements: async (boxId, params = {}, options = {}) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.BOX_MEASUREMENTS(boxId),
        params
      );

      // Default options preserve BME688 hierarchy
      const transformOptions = {
        preserveBME688Hierarchy: true,
        ...options
      };

      // Handle paginated or array responses
      if (Array.isArray(response)) {
        return response.map(item => {
          const extracted = extractMeasurementData(item);
          const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
          return transformMeasurementResponse(parsed, transformOptions);
        });
      }

      const extracted = extractMeasurementData(response);
      const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
      return transformMeasurementResponse(parsed, transformOptions);
    } catch (error) {
      console.error(`Error fetching box measurements for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getBoxMeasurements');
    }
  },

  // Get measurements with date range
  getMeasurementsInRange: async (boxId, startDate, endDate, params = {}, options = {}) => {
    try {
      if (!boxId || !startDate || !endDate) {
        throw new Error('Box ID, start date, and end date are required');
      }

      // Format dates to ISO string if they're Date objects
      const formattedStartDate = startDate instanceof Date ? startDate.toISOString() : startDate;
      const formattedEndDate = endDate instanceof Date ? endDate.toISOString() : endDate;

      const queryParams = {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        ...params
      };

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.BOX_MEASUREMENTS(boxId),
        queryParams
      );

      // Default options preserve BME688 hierarchy
      const transformOptions = {
        preserveBME688Hierarchy: true,
        ...options
      };

      // Handle paginated or array responses
      if (Array.isArray(response)) {
        return response.map(item => {
          const extracted = extractMeasurementData(item);
          const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
          return transformMeasurementResponse(parsed, transformOptions);
        });
      }

      const extracted = extractMeasurementData(response);
      const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
      return transformMeasurementResponse(parsed, transformOptions);
    } catch (error) {
      console.error(`Error fetching measurements in range for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getMeasurementsInRange');
    }
  },

  // Get measurements with pagination
  getMeasurementsPaginated: async (boxId, page = 1, limit = 100, params = {}, options = {}) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      if (page < 1) page = 1;
      if (limit < 1) limit = 100;
      if (limit > 1000) limit = 1000; // Reasonable upper limit

      const queryParams = {
        page,
        limit,
        ...params
      };

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.BOX_MEASUREMENTS(boxId),
        queryParams
      );

      // Default options preserve BME688 hierarchy
      const transformOptions = {
        preserveBME688Hierarchy: true,
        ...options
      };

      // Handle different response formats
      if (response.results && Array.isArray(response.results)) {
        // Paginated response
        return {
          results: response.results.map(item => {
            const extracted = extractMeasurementData(item);
            const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
            return transformMeasurementResponse(parsed, transformOptions);
          }),
          total: response.total || response.count || response.results.length,
          page: response.page || page,
          limit: response.limit || limit,
          has_next: response.has_next || false,
          has_previous: response.has_previous || false
        };
      } else if (Array.isArray(response)) {
        // Simple array response
        return {
          results: response.map(item => {
            const extracted = extractMeasurementData(item);
            const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
            return transformMeasurementResponse(parsed, transformOptions);
          }),
          total: response.length,
          page: 1,
          limit: response.length,
          has_next: false,
          has_previous: false
        };
      }

      // Single item response
      const extracted = extractMeasurementData(response);
      const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
      return {
        results: [transformMeasurementResponse(parsed, transformOptions)],
        total: 1,
        page: 1,
        limit: 1,
        has_next: false,
        has_previous: false
      };
    } catch (error) {
      console.error(`Error fetching paginated measurements for box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getMeasurementsPaginated');
    }
  },

  // Get sensor-specific measurements (existing method - for compatibility)
  getSensorMeasurements: async (boxId, sensorName, params = {}, options = {}) => {
    try {
      if (!boxId || !sensorName) {
        throw new Error('Box ID and sensor name are required');
      }

      const queryParams = {
        sensor: sensorName,
        ...params
      };

      const response = await apiService.get(
        API_CONFIG.ENDPOINTS.BOX_MEASUREMENTS(boxId),
        queryParams
      );

      // For sensor-specific queries, we might want to preserve hierarchy
      const transformOptions = {
        preserveBME688Hierarchy: sensorName === 'bme688',
        ...options
      };

      // Filter measurements for the specific sensor
      if (Array.isArray(response)) {
        return response
          .map(item => {
            const extracted = extractMeasurementData(item);
            const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
            return transformMeasurementResponse(parsed, transformOptions);
          })
          .filter(item => {
            if (!item.measurement_data) return false;
            // Check for exact match or BME688 profile match
            return item.measurement_data[sensorName] ||
                   Object.keys(item.measurement_data).some(key => key.startsWith(sensorName));
          });
      }

      const extracted = extractMeasurementData(response);
      const parsed = apiUtils.parseApiResponse(extracted, 'measurements');
      const transformed = transformMeasurementResponse(parsed, transformOptions);

      if (transformed.measurement_data &&
          (transformed.measurement_data[sensorName] ||
           Object.keys(transformed.measurement_data).some(key => key.startsWith(sensorName)))) {
        return transformed;
      }

      return {
        box_id: boxId,
        measurement_data: {},
        sensor_name: sensorName,
        error: 'No data found for this sensor'
      };
    } catch (error) {
      console.error(`Error fetching sensor measurements for ${sensorName} in box ${boxId}:`, error);
      throw apiUtils.handleApiError(error, 'getSensorMeasurements');
    }
  },

  // Check if measurements are available for a box
  checkMeasurementAvailability: async (boxId, options = {}) => {
    try {
      if (!boxId) {
        throw new Error('Box ID is required');
      }

      const response = await measurementsService.getLatestMeasurements(boxId, options);

      const sensorCount = response.measurement_data ? Object.keys(response.measurement_data).length : 0;
      const activeSensorCount = response.measurement_data ?
        Object.values(response.measurement_data).filter(data =>
          data !== null && data !== undefined &&
          (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)
        ).length : 0;

      return {
        available: sensorCount > 0,
        sensor_count: sensorCount,
        active_sensor_count: activeSensorCount,
        last_measurement: response.timestamp,
        freshness: apiUtils.getDataFreshness(response.timestamp)
      };
    } catch (error) {
      return {
        available: false,
        sensor_count: 0,
        active_sensor_count: 0,
        last_measurement: null,
        freshness: 'unknown',
        error: apiUtils.formatErrorMessage(error)
      };
    }
  },

  // Utility function to get original measurement data if needed
  getOriginalMeasurementData: (transformedResponse) => {
    return transformedResponse._original_measurement_data || transformedResponse.measurement_data;
  }
};